import React from "react";
import { Link } from "react-router-dom";
import {
  Landmark,
  Scale,
  GraduationCap,
  Newspaper,
  Users,
  FileUp,
  Calculator,
  BarChart3,
  FileOutput,
  ArrowRight,
} from "lucide-react";
import ChapterShell from "@/tutorial/ChapterShell";
import {
  Section,
  Quote,
  WarningBox,
} from "@/tutorial/components/TutorialComponents";

const PIPELINE = [
  { icon: FileUp,      label: "PDF Budget",      target: 2 },
  { icon: Calculator,  label: "Calcul vitaliste", target: 4 },
  { icon: BarChart3,   label: "Graphiques",      target: 5 },
  { icon: FileOutput,  label: "Rapport",         target: 8 },
];

const PROFILES = [
  {
    icon: Landmark,
    title: "Parlementaires",
    desc: "Traduire en vies humaines l'arbitrage politique entre dépenses, fiscalité et dette.",
  },
  {
    icon: Scale,
    title: "Juristes & magistrats",
    desc: "Qualifier les infractions aux finances publiques et illustrer pédagogiquement les peines.",
  },
  {
    icon: GraduationCap,
    title: "Chercheurs",
    desc: "Produire des analyses reproductibles et citables, comparables entre exercices et pays.",
  },
  {
    icon: Newspaper,
    title: "Journalistes",
    desc: "Convertir un budget en infographies grand public, claires, imprimables, partageables.",
  },
  {
    icon: Users,
    title: "ONG de transparence",
    desc: "Objectiver les coûts sociaux d'un choix budgétaire en jours de vie sacrifiés.",
  },
];

export default function Chapter0() {
  return (
    <ChapterShell id={0}>
      <Section id="section-0-1" eyebrow="0.1" title="La question fondamentale">
        <Quote author="VITAE-PUBLICA™ — manifeste fondateur">
          Combien de jours de votre vie avez-vous consacrés à payer vos impôts
          cette année ?
        </Quote>
        <p>
          VITAE-PUBLICA™ repose sur une thèse épistémologique forte : l'unité
          de compte des finances publiques n'est pas la monnaie, mais la{" "}
          <strong>vie humaine</strong>. Chaque impôt, chaque taxe, chaque
          cotisation est une tranche d'existence prélevée au contribuable. Chaque
          dette souveraine est une hypothèque silencieuse sur la vie future des
          générations à naître.
        </p>
        <p>
          Cette application transforme ce postulat en un outil quantitatif :
          elle lit un budget public, l'évalue, et restitue en{" "}
          <em>jours de vie sacrifiés</em> ce que les politiques budgétaires
          demandent réellement aux citoyens.
        </p>
      </Section>

      <Section id="section-0-2" eyebrow="0.2" title="Ce que fait VITAE-PUBLICA™">
        <p>
          Quatre étapes enchaînées composent la chaîne vitaliste. Cliquez sur
          chaque bloc pour accéder au chapitre correspondant.
        </p>
        <div className="grid grid-cols-1 items-center gap-3 sm:grid-cols-7">
          {PIPELINE.map((p, i) => {
            const Icon = p.icon;
            return (
              <React.Fragment key={p.label}>
                <Link
                  to={`/tutorial/chapter/${p.target}`}
                  data-testid={`chapter-0-pipeline-${p.target}`}
                  className="group col-span-1 flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 text-center transition-all hover:-translate-y-0.5 hover:border-brand-gold/60 hover:shadow-md sm:col-span-1"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-blue text-white transition-colors group-hover:bg-brand-gold group-hover:text-background">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-semibold">{p.label}</span>
                </Link>
                {i < PIPELINE.length - 1 && (
                  <ArrowRight className="mx-auto hidden h-5 w-5 text-brand-gold sm:block" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </Section>

      <Section
        id="section-0-3"
        eyebrow="0.3"
        title="À qui s'adresse cette application ?"
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PROFILES.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-gold/15 text-brand-gold">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="font-semibold">{p.title}</div>
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {p.desc}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      <Section id="section-0-4" eyebrow="0.4" title="Avertissement légal">
        <WarningBox title="Document strictement pédagogique">
          Toutes les analyses, qualifications juridiques et simulations de
          jugement produites par VITAE-PUBLICA™ sont{" "}
          <strong>à but pédagogique uniquement</strong>. Elles ne constituent
          en aucun cas une décision juridictionnelle, une accusation nominative
          ou un avis juridique officiel. L'application raisonne par catégories
          (« les ordonnateurs », « les décideurs budgétaires ») et ne désigne
          jamais de personnes ou d'administrations nommément.
        </WarningBox>
      </Section>
    </ChapterShell>
  );
}
